import { useMemo } from "react";
import { useAccessManagement, type UseAccessManagementOptions } from "./useAccessManagement";
import type { UserListItem, UserPermissionChip, UserPermissionRole } from "../components/UserListTable/UserListTable";

function parseSiteRole(role: string): { type: "site_manager" | "site_user"; siteId: string } | null {
  if (role.startsWith("site_manager:")) return { type: "site_manager", siteId: role.substring("site_manager:".length) };
  if (role.startsWith("site_user:")) return { type: "site_user", siteId: role.substring("site_user:".length) };
  return null;
}

function buildPermissionChips(
  userId: string,
  roles: Array<{ user: string; role: string }>,
  cameras: Array<{ user: string; camera: string; role: string }>,
): UserPermissionChip[] {
  const chips: UserPermissionChip[] = [];

  const userRoles = roles.filter((r) => r.user === userId);
  const userCameras = cameras.filter((c) => c.user === userId);

  const isTenantAdmin = userRoles.some((r) => r.role === "tenant_admin");
  if (isTenantAdmin) {
    chips.push({ label: "Tenant Admin", role: "tenant_admin" });
  }

  for (const assignment of userRoles) {
    const parsed = parseSiteRole(assignment.role);
    if (parsed) {
      const role: UserPermissionRole = parsed.type === "site_manager" ? "site_manager" : "site_viewer";
      chips.push({ label: parsed.siteId, role, siteId: parsed.siteId });
    }
  }

  for (const cam of userCameras) {
    const role: UserPermissionRole = cam.role === "camera_manager" ? "camera_manager" : "camera_viewer";
    const siteId = cam.camera.includes("/") ? cam.camera.substring(0, cam.camera.indexOf("/")) : undefined;
    chips.push({ label: cam.camera, role, cameraId: cam.camera, ...(siteId ? { siteId } : {}) });
  }

  return chips;
}

/**
 * Hook that fetches access management data and transforms it into
 * `UserListItem[]` for rendering in `UserListTable`.
 */
export function useUserList(options: UseAccessManagementOptions = {}) {
  const { data, isLoading, error } = useAccessManagement(options);

  const users: UserListItem[] = useMemo(() => {
    if (!data) return [];

    const rolesWithStringIds = data.roles.map((r) => ({
      user: r.user as string,
      role: r.role as string,
    }));

    const camerasWithStringIds = data.cameras.map((c) => ({
      user: c.user as string,
      camera: c.camera,
      role: c.role,
    }));

    return data.users.map((user) => {
      const userId = user.id as string;
      const name = [user.givenName, user.familyName].filter(Boolean).join(" ");

      const item: UserListItem = {
        id: userId,
        name: name || user.email,
        permissions: buildPermissionChips(userId, rolesWithStringIds, camerasWithStringIds),
      };
      if (name) item.email = user.email;
      return item;
    });
  }, [data]);

  return { users, isLoading, error };
}
